import { DatePicker } from '@mui/lab';
import { Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useCallback, useEffect } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Italian from '../../shared/italian';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ActFormVM } from 'civil-status/view-models/step-form';
import { ItemVM } from 'shell/view-models/item';
import ListAnsc from '../../../../../../shared/list-ansc';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';

interface ActFormProps {
  disabled?: boolean;
  values?: ActFormVM;
  errors?: FormikErrors<ActFormVM>;
  touched?: FormikTouched<ActFormVM>;
  onChange: (key: keyof ActFormVM, value: ActFormVM[keyof ActFormVM]) => void;
  onChanges: (values: ActFormVM) => void;
  configuration?: string;
  isPaper?: boolean;
  nothingRequired?: boolean;
  withPresentationDocument?: boolean;
  idCat?: string;
  isRequiredData?: boolean;
  isRactod343?: boolean;
  register?: string;
  municipality?: MunicipalityAnprVM;
}

const ActFormComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  isPaper = false,
  nothingRequired = false,
  withPresentationDocument = false,
  isRequiredData = false,
  isRactod343 = false,
  register,
  municipality,
}: ActFormProps): React.ReactElement => {
  useEffect(() => {
    if (register || municipality) {
      onChanges({
        ...values,
        register: register || values?.register,
        disabledRegister: Boolean(register),
        italian: municipality
          ? {
              municipality,
              disabledMunicipality: true,
            }
          : values?.italian,
      });
    }
  }, [register, municipality]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const handleListTypeEventChange = useEventCallback((item?: ItemVM) => {
    onChange('typeEvent', item?.id?.toString() || '');
  });

  const handleListRegisterChange = useEventCallback((item?: ItemVM) => {
    onChange('register', item?.id?.toString() || '');
  });

  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id?.toString());
  });

  return (
    <Grid container spacing={2} item xs={12}>
      {isPaper ? undefined : (
        <Grid item xs={12} md={3}>
          <PmiTextField
            aria-label="ID Ansc"
            label={`ID Ansc ${nothingRequired ? '' : '*'}`}
            id="idAnsc"
            name="idAnsc"
            fullWidth
            type="text"
            variant="outlined"
            value={values?.idAnsc || ''}
            onChange={(event) => {
              onChange('idAnsc', event.target.value);
            }}
            helperText={errors?.idAnsc}
            error={touched?.idAnsc && Boolean(errors?.idAnsc)}
            disabled={disabled}
          />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <DatePicker
          label={`Data atto ${isRequiredData && !nothingRequired ? '*' : ''}`}
          aria-label="Data atto"
          value={values?.dateAct || null}
          onChange={(date) => {
            onChange('dateAct', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateAct"
              fullWidth
              helperText={errors?.dateAct}
              error={touched?.dateAct && Boolean(errors?.dateAct)}
            />
          )}
        />
      </Grid>
      <Italian
        disabledComponent={values?.italian?.disabledMunicipality || disabled}
        values={values?.italian}
        onChange={onChangeItalian}
        onChanges={onChangesItalian}
        errors={errors?.italian as FormikErrors<ItalianForm>}
        touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
        labelMunicipality={`Comune ente ${nothingRequired ? '' : '*'}`}
        labelProvince={`Provincia ente ${nothingRequired ? '' : '*'}`}
        fromDate={values?.dateAct || null}
      />
      <Grid item xs={12} md={3}>
        <ListAnsc
          label="Tipo evento"
          idDominio="2"
          value={values?.typeEvent || ''}
          onChange={handleListTypeEventChange}
          disabled={disabled}
          error={Boolean(errors?.typeEvent) && touched?.typeEvent}
          required={!nothingRequired}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListAnsc
          label="Registro"
          idDominio="1"
          value={values?.register || ''}
          onChange={handleListRegisterChange}
          disabled={values?.disabledRegister || disabled}
          error={Boolean(errors?.register) && touched?.register}
          required={!nothingRequired}
        />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={3}>
          <PmiTextField
            label={`Anno ${nothingRequired ? '' : '*'}`}
            aria-label="Anno"
            id="year"
            fullWidth
            inputProps={{
              maxLength: '4',
            }}
            onChange={(event) => {
              onChange('year', event.target.value);
            }}
            value={values?.year || ''}
            helperText={errors?.year}
            error={touched?.year && Boolean(errors?.year)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            aria-label="Numero comunale"
            label={`Numero comunale ${nothingRequired ? '' : '*'}`}
            id="municipalNumber"
            name="municipalNumber"
            fullWidth
            type="text"
            disabled={disabled}
            variant="outlined"
            value={values?.municipalNumber || ''}
            onChange={(event) => onChange('municipalNumber', event.target.value)}
            error={touched?.municipalNumber && Boolean(errors?.municipalNumber)}
            helperText={errors?.municipalNumber}
          />
        </Grid>
        {isPaper ? (
          <Fragment>
            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Parte"
                label="Parte"
                id="portion"
                name="portion"
                fullWidth
                type="text"
                variant="outlined"
                inputProps={{
                  maxLength: '1',
                }}
                value={values?.portion || ''}
                onChange={(event) => {
                  onChange('portion', event.target.value);
                }}
                helperText={errors?.portion}
                error={touched?.portion && Boolean(errors?.portion)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Serie"
                label="Serie"
                id="serie"
                name="serie"
                fullWidth
                type="text"
                disabled={disabled}
                inputProps={{
                  maxLength: '2',
                }}
                variant="outlined"
                value={values?.serie || ''}
                onChange={(event) => onChange('serie', event.target.value)}
                error={touched?.serie && Boolean(errors?.serie)}
                helperText={errors?.serie}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiTextField
                aria-label="Volume"
                label="Volume"
                id="volume"
                name="volume"
                fullWidth
                type="text"
                disabled={disabled}
                variant="outlined"
                value={values?.volume || ''}
                onChange={(event) => onChange('volume', event.target.value)}
                error={touched?.volume && Boolean(errors?.volume)}
                helperText={errors?.volume}
              />
            </Grid>
          </Fragment>
        ) : undefined}
      </Grid>
      {withPresentationDocument ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Presentazione documenti"
            idDominio="36"
            value={values?.presentationDocument || ''}
            onChange={handleListPresentationDocument}
            disabled={disabled}
            error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
          />
        </Grid>
      ) : undefined}
      {isRactod343 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Sposo"
              aria-label="Sposo"
              id="nameSurnameGroom"
              name="nameSurnameGroom"
              fullWidth
              type="text"
              disabled={disabled}
              variant="outlined"
              value={values?.nameSurnameGroom || ''}
              onChange={(event) => onChange('nameSurnameGroom', event.target.value)}
              error={touched?.nameSurnameGroom && Boolean(errors?.nameSurnameGroom)}
              helperText={errors?.nameSurnameGroom}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Sposa"
              aria-label="Sposa"
              id="nameSurnameWife"
              name="nameSurnameWife"
              fullWidth
              type="text"
              disabled={disabled}
              variant="outlined"
              value={values?.nameSurnameWife || ''}
              onChange={(event) => onChange('nameSurnameWife', event.target.value)}
              error={touched?.nameSurnameWife && Boolean(errors?.nameSurnameWife)}
              helperText={errors?.nameSurnameWife}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              aria-label="Codice Usecase"
              label="Codice Usecase"
              id="useCase"
              name="useCase"
              fullWidth
              type="text"
              disabled={disabled}
              variant="outlined"
              value={values?.useCase || ''}
              onChange={(event) => onChange('useCase', event.target.value)}
              error={touched?.useCase && Boolean(errors?.useCase)}
              helperText={errors?.useCase}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              aria-label="Descrizione Usecase"
              label="Descrizione Usecase"
              id="descUseCase"
              name="descUseCase"
              fullWidth
              type="text"
              disabled={disabled}
              variant="outlined"
              value={values?.descUseCase || ''}
              onChange={(event) => onChange('descUseCase', event.target.value)}
              error={touched?.descUseCase && Boolean(errors?.descUseCase)}
              helperText={errors?.descUseCase}
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

ActFormComponent.displayName = 'Form Act Form component';

const ActForm = memo(ActFormComponent);

export default ActForm;
